/**
 * Camelot codes and traditional key names, in both directions.
 *
 * Camelot is a code for mixing: adjacent numbers are a fifth apart, A and B are
 * relative minor and major, so "compatible" is arithmetic. That is exactly why
 * it is unreadable — 8A carries no hint that it means A minor, and a curator
 * writing a rota, briefing a presenter or talking to a musician needs the name.
 *
 * Nova stores both and shows both. This table is the mapping; the engine
 * computes the same thing independently from the pitch class, and a test pins
 * the two against each other so they cannot drift.
 */

export interface KeyNaming {
  camelot: string;
  /** Traditional name, e.g. "A minor". */
  musical: string;
  /** Root note without the mode, e.g. "A". */
  root: string;
  mode: "major" | "minor";
}

/**
 * The wheel. Position n on the A ring is the relative minor of position n on
 * the B ring, and n+1 is a fifth up — which is the whole point of the notation.
 */
export const CAMELOT_WHEEL: KeyNaming[] = [
  { camelot: "1A", musical: "A♭ minor", root: "A♭", mode: "minor" },
  { camelot: "1B", musical: "B major", root: "B", mode: "major" },
  { camelot: "2A", musical: "E♭ minor", root: "E♭", mode: "minor" },
  { camelot: "2B", musical: "F♯ major", root: "F♯", mode: "major" },
  { camelot: "3A", musical: "B♭ minor", root: "B♭", mode: "minor" },
  { camelot: "3B", musical: "D♭ major", root: "D♭", mode: "major" },
  { camelot: "4A", musical: "F minor", root: "F", mode: "minor" },
  { camelot: "4B", musical: "A♭ major", root: "A♭", mode: "major" },
  { camelot: "5A", musical: "C minor", root: "C", mode: "minor" },
  { camelot: "5B", musical: "E♭ major", root: "E♭", mode: "major" },
  { camelot: "6A", musical: "G minor", root: "G", mode: "minor" },
  { camelot: "6B", musical: "B♭ major", root: "B♭", mode: "major" },
  { camelot: "7A", musical: "D minor", root: "D", mode: "minor" },
  { camelot: "7B", musical: "F major", root: "F", mode: "major" },
  { camelot: "8A", musical: "A minor", root: "A", mode: "minor" },
  { camelot: "8B", musical: "C major", root: "C", mode: "major" },
  { camelot: "9A", musical: "E minor", root: "E", mode: "minor" },
  { camelot: "9B", musical: "G major", root: "G", mode: "major" },
  { camelot: "10A", musical: "B minor", root: "B", mode: "minor" },
  { camelot: "10B", musical: "D major", root: "D", mode: "major" },
  { camelot: "11A", musical: "F♯ minor", root: "F♯", mode: "minor" },
  { camelot: "11B", musical: "A major", root: "A", mode: "major" },
  { camelot: "12A", musical: "D♭ minor", root: "D♭", mode: "minor" },
  { camelot: "12B", musical: "E major", root: "E", mode: "major" },
];

const BY_CAMELOT = new Map(CAMELOT_WHEEL.map((k) => [k.camelot.toUpperCase(), k]));

/**
 * Spelling variants that must all resolve.
 *
 * Enharmonics are the awkward part: A♭ minor and G♯ minor are the same pitches
 * and a tagger may have written either, so both point at one entry. Nova does
 * not try to decide which spelling is "correct" — that depends on the music, and
 * guessing would be inventing an opinion the audio cannot support.
 */
function variants(k: KeyNaming): string[] {
  const flat = k.musical;
  const ascii = flat.replace(/♭/g, "b").replace(/♯/g, "#");
  const sharps: Record<string, string> = {
    "A♭": "G♯", "E♭": "D♯", "B♭": "A♯", "D♭": "C♯", "G♭": "F♯",
  };
  const enharmonic = sharps[k.root] ? `${sharps[k.root]} ${k.mode}` : null;

  return [
    flat,
    ascii,
    ascii.replace(" ", ""),
    k.mode === "minor" ? `${ascii.split(" ")[0]}m` : ascii.split(" ")[0],
    ...(enharmonic ? [enharmonic, enharmonic.replace(/♯/g, "#")] : []),
  ];
}

const BY_NAME = new Map<string, KeyNaming>();
for (const k of CAMELOT_WHEEL) {
  for (const v of variants(k)) BY_NAME.set(v.toLowerCase().replace(/\s+/g, " ").trim(), k);
}

/** Look up a key by Camelot code or by any accepted musical spelling. */
export function lookupKey(value: string | null | undefined): KeyNaming | null {
  if (!value) return null;
  const trimmed = value.trim();
  return (
    BY_CAMELOT.get(trimmed.toUpperCase()) ??
    BY_NAME.get(trimmed.toLowerCase().replace(/\s+/g, " ")) ??
    null
  );
}

/** The traditional name for a Camelot code, or the input if it is not one. */
export function musicalName(camelot: string | null | undefined): string | null {
  if (!camelot) return null;
  return lookupKey(camelot)?.musical ?? camelot;
}

/** The Camelot code for anything nameable, so search can accept either form. */
export function toCamelot(value: string | null | undefined): string | null {
  return lookupKey(value)?.camelot ?? null;
}
